/**
 * Os links da campanha (P-51 do plano do pacote Campanhas + DV360; DS-660) — contrato próprio, fora de
 * `campanhas.ts`, para a etapa dos links não disputar o arquivo da campanha com as outras.
 *
 * Rotas (todas sob a campanha, e a URL do link só no CORPO JSON, nunca em query string):
 *   GET    /campanhas/:id/links                      → `LinksDaCampanha` (ativos visíveis, mais novo primeiro)
 *   GET    /campanhas/:id/links/:linkId              → `LinkDaCampanha`
 *   POST   /campanhas/:id/links                      → 201 `LinkDaCampanha`   (corpo `EntradaDeLinkDaCampanha`)
 *   PATCH  /campanhas/:id/links/:linkId              → 200 `LinkDaCampanha`   (corpo `EdicaoDeLinkDaCampanha`)
 *   DELETE /campanhas/:id/links/:linkId              → 200 `{ ok: true }`     (exclusão lógica)
 *   POST   /campanhas/:id/links/:linkId/restaurar    → 201 `LinkDaCampanha`   (o Desfazer, até 15 min, por quem excluiu)
 *
 * Para quem não vê custos e margem, o link restrito não existe: 404 em todas as rotas, e a lista não o traz. As
 * regras (título, endereço, host, limite e a marca de restrito) são do domínio, e o contrato as reexporta para a tela
 * avisar antes de enviar com as mesmas frases.
 */
import { z } from 'zod'
import { uuid } from './comum.js'
import { referenciaDeUsuario } from './usuarios.js'

export {
  hostDoLink,
  LIMITE_DE_LINKS_ATIVOS,
  mensagemDaRecusaDoLink,
  recusaDaUrlDoLink,
  recusaDoTituloDoLink,
  TAMANHO_MAXIMO_DA_URL_DO_LINK,
  TAMANHO_MAXIMO_DO_TITULO_DO_LINK,
  validarConteudoDoLink,
  type RecusaDoLink,
} from '@dv/domain'

/** Um link ativo, como a lista e a ficha o mostram. `host` vem pronto do servidor (a tela não o deriva). */
export const linkDaCampanha = z.object({
  id: uuid,
  campanhaId: uuid,
  titulo: z.string(),
  url: z.string(),
  host: z.string(),
  restrito: z.boolean(),
  autor: referenciaDeUsuario,
  /** O instante da criação (ISO 8601, UTC). */
  criadoEm: z.string(),
})
export type LinkDaCampanha = z.infer<typeof linkDaCampanha>

/**
 * O que a pessoa pode fazer na seção, já cruzado com o status da campanha (a tabela única do §5.5):
 *   · `podeAdicionar`: edita a campanha, o status admite links e a campanha está abaixo do limite;
 *   · `podeEditar`: edita a campanha e o status admite links (até a Concluída);
 *   · `podeExcluir`: edita a campanha (em qualquer status: na Cancelada, só ler e excluir). O Desfazer do aviso de
 *     exclusão (restaurar) segue `podeEditar`, não `podeExcluir`: na Cancelada o aviso sai sem Desfazer;
 *   · `podeMarcarRestrito`: pode editar os links e vê custos e margem (só ela marca e desmarca);
 *   · `limiteAtingido`: a campanha tem o limite de links ativos (os restritos contam).
 */
export const capacidadesDosLinks = z.object({
  podeAdicionar: z.boolean(),
  podeEditar: z.boolean(),
  podeExcluir: z.boolean(),
  podeMarcarRestrito: z.boolean(),
  limiteAtingido: z.boolean(),
})
export type CapacidadesDosLinks = z.infer<typeof capacidadesDosLinks>

export const linksDaCampanha = z.object({
  links: z.array(linkDaCampanha),
  /** Quantos links ativos a campanha pode ter (50). */
  limite: z.number().int(),
  capacidades: capacidadesDosLinks,
})
export type LinksDaCampanha = z.infer<typeof linksDaCampanha>

/**
 * O corpo do Adicionar link. O título e o endereço são conferidos pelo domínio (422 nomeado por campo:
 * `campanha_link.titulo_obrigatorio`, `…titulo_longo`, `…titulo_com_controle`, `…url_nao_https`, `…url_longa`,
 * `…url_invalida`, `…url_com_credenciais`); `restrito` ausente é falso.
 */
export const entradaDeLinkDaCampanha = z.object({
  titulo: z.string(),
  url: z.string(),
  restrito: z.boolean().optional(),
})
export type EntradaDeLinkDaCampanha = z.input<typeof entradaDeLinkDaCampanha>

/** O corpo do Editar link: só o que vem muda; reenviar o mesmo valor não é mudança (nada se grava). */
export const edicaoDeLinkDaCampanha = z.object({
  titulo: z.string().optional(),
  url: z.string().optional(),
  restrito: z.boolean().optional(),
})
export type EdicaoDeLinkDaCampanha = z.input<typeof edicaoDeLinkDaCampanha>
